import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Input, Button, CardBody } from 'reactstrap';
import { FaWallet, FaGlobe, FaFile, FaShoppingCart, FaEye, FaEdit } from 'react-icons/fa';
import { FaFileAlt, FaClipboardList, FaFileInvoiceDollar, FaBoxOpen, FaBan, FaCoins, FaChartLine } from 'react-icons/fa';
import DataTable from 'react-data-table-component';
import { useNavigate } from 'react-router-dom';
import DashboardService from "../dashboard.service.js";
import AuthService from "../../Authentication/auth.service.js";
import { useTranslation } from 'react-i18next';
import PermissionAccess from '../../../PermissionAccess/Access.js';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip
} from 'recharts';
import QuotationsRequested from './QuotationsRequested.js';
import moment from "moment";
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import styled from 'styled-components';

// Updated styled components for the banner
const BannerContainer = styled.div`
  position: relative;
  height: 400px;
  overflow: hidden;
  border-radius: 20px;
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.3);
  margin: 30px 0;
  background: transparent;
`;

const BannerImageWrapper = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
`;

const BannerImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
`;

const BannerOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 50%, rgba(0,0,0,0) 100%);
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 40px;
  color: white;
`;

const BannerTitle = styled.h3`
  margin: 0;
  font-size: 36px;
  font-weight: bold;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.5);
  max-width: 80%;
`;

const BannerSubtitle = styled.p`
  font-size: 20px;
  margin: 15px 0 0 0;
  text-shadow: 1px 1px 2px rgba(0,0,0,0.5);
  max-width: 70%;
`;

const AdLabel = styled.span`
  position: absolute;
  top: 20px;
  right: 20px;
  background-color: rgba(255, 255, 255, 0.2);
  color: white;
  padding: 5px 15px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: bold;
  backdrop-filter: blur(5px);
`;


const Home = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const authData = JSON.parse(localStorage.getItem("user"));
  const sessionID = localStorage.getItem("session-id");
  const [banners, setBanners] = useState([]);
  const [dashboardData, setDashboardData] = useState({
    trendData: [],
    purchaseData: [],
    latestRFQs: []
  });

  useEffect(() => {
    i18n.changeLanguage(localStorage.getItem('language'));
    fetchDashboardData();
    fetchActiveBanners();
  }, []);

  const fetchActiveBanners = () => {
    setLoading(true);
    DashboardService.getActiveBanners()
      .then((response) => {
        setLoading(false);
        if (response.data.status) {
          setBanners(response.data.data);
        } else {
          //iph todo: 47
          //console.error("Failed to fetch active banners");
        }
      })
      .catch((error) => {
        setLoading(false);
        console.error("Error fetching active banners:", error);
      });
  };

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 5000,
    arrows: true,
    fade: true,
  };

  const fetchDashboardData = () => {
    setLoading(true);
    DashboardService.getDashboardData(authData._id, sessionID)
      .then((response) => {
        setLoading(false);
        if (response.data.status) {
          setDashboardData(response.data.data);
        } else {
          // Handle error
          //iph todo: 47
          //console.error("Failed to fetch dashboard data");
        }
      })
      .catch((error) => {
        setLoading(false);
        console.error("Error fetching dashboard data:", error);
        if (error.response && error.response.data.message === "Unauthorized") {
          AuthService.logout();
          navigate("/login");
          window.location.reload();
        }
      });
  };

  const handleRowClick = (row) => {
    navigate(`/e-invoice-details/${row.id}`, { state: row });
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return '#FFA500'; // Orange
      case 'confirmed':
      case 'accepted':
        return '#4169E1'; // Royal Blue
      case 'processing':
        return '#9370DB'; // Medium Purple
      case 'in_transit':
        return '#1E90FF'; // Dodger Blue
      case 'delivered':
        return '#32CD32'; // Lime Green
      case 'cancelled':
      case 'rejected':
        return '#DC143C'; // Crimson
      case 'issued':
        return '#228B22'; // Forest Green
      case 'review':
        return '#FF4500'; // Orange Red
      default:
        return '#808080'; // Gray for unknown status
    }
  };

  const columns = [
    { name: 'Order ID', selector: row => row?.rfq_id, sortable: true },
    { name: 'Total Item', selector: row => row?.items?.length, sortable: true },
    { name: 'Date of Order', selector: row => moment(row?.datetime_added).format('DD-MM-YYYY'), sortable: true },
    {
      name: 'Status',
      selector: row => row?.status?.charAt(0)?.toUpperCase() + row?.status?.slice(1),
      sortable: true,
      cell: row => {
        const capitalizedStatus = row?.status?.charAt(0)?.toUpperCase() + row?.status?.slice(1);
        return (
          <span style={{
            color: getStatusColor(row?.status),
            fontWeight: 'bold',
          }}>
            {capitalizedStatus}
          </span>
        );
      }
    },
    {
      name: 'Payment Status',
      selector: row => row?.payment_status ? row?.payment_status?.charAt(0)?.toUpperCase() + row?.payment_status?.slice(1) : 'N/A',
      sortable: true,
      cell: row => {
        const capitalizedStatus = row?.payment_status ? row?.payment_status?.charAt(0)?.toUpperCase() + row?.payment_status?.slice(1) : 'N/A';
        return (
          <span style={{
            color: getStatusColor(row?.payment_status),
            fontWeight: 'bold',
          }}>
            {capitalizedStatus}
          </span>
        );
      }
    },
    {
      name: 'Action',
      cell: row => (
        <div>
          <Button color="link" onClick={() => navigate(`/view_rfqs/${row._id}`)} className="p-0 me-2">
            <FaEye size={18} color="#3D607A" />
          </Button>
          {row.status === 'requested' && (
            <Button color="link" onClick={() => navigate(`/edit_rfqs/${row._id}`)} className="p-0">
              <FaEdit size={18} color="#3D607A" />
            </Button>
          )}
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ];
  return (
    <div className="app-main__outer">
      <div className="container mt-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h1 style={{ fontWeight: '900', fontSize: '26px' }}>Dashboard</h1>
          <div className="d-flex">
            {/* <Input type="text" placeholder="Search for something" className="mr-2" /> */}
          </div>
        </div>

        <Row className="g-4 mb-4">
          {dashboardData.trendData.map((trend, index) => (
            <Col md={3} key={index}>
              <Card body className="trend-card d-flex flex-row align-items-center justify-content-between">
                <div>
                  <div className="trend-title">{trend.title}</div>
                  <div className="trend-value">{trend.value}</div>
                </div>
                <div className="icon-circle" style={{ backgroundColor: '#3D607A' }}>
                  {trend.title.includes('Orders') ? <FaFileAlt /> :
                    trend.title.includes('Pending Orders') ? <FaClipboardList /> :
                      trend.title.includes('Quotations Received') ? <FaFileInvoiceDollar /> :
                        trend.title.includes('Delivered Orders') ? <FaBoxOpen /> :
                          trend.title.includes('Cancelled Orders') ? <FaBan /> :
                            trend.title.includes('Daily Purchases') ? <FaCoins /> :
                              trend.title.includes('Monthly Purchases') ? <FaChartLine /> :
                                trend.title.includes('Total Purchases') ? <FaShoppingCart /> :
                                  <FaShoppingCart />}
                </div>
              </Card>
            </Col>
          ))}
        </Row>

        <Row>
          <Col md={12}>
            <Slider {...sliderSettings}>
              {banners.map((banner, index) => (
                <div key={index}>
                  <BannerContainer>
                    <BannerImageWrapper>
                      <BannerImage
                        src={`${process.env.REACT_APP_IMAGEURL}${banner.Image[0]}`}
                        alt={banner.title}
                      />
                    </BannerImageWrapper>
                    <BannerOverlay>
                      <div>
                        <BannerTitle>{banner.title}</BannerTitle>
                        <BannerSubtitle>Valid until: {new Date(banner.endDate).toLocaleDateString()}</BannerSubtitle>
                      </div>
                    </BannerOverlay>
                    <AdLabel>Ad</AdLabel>
                  </BannerContainer>
                </div>
              ))}
            </Slider>
          </Col>
        </Row>

        <Row className="g-4">
          <Col md={8}>
            <Card body className="chart-card">
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="m-0">Total Purchases</h5>
                  <div className="d-flex align-items-center">
                    {/* <span className="me-2">Sort by</span>
                    <Input type="select" bsSize="sm" style={{ width: '120px' }}>
                      <option>Monthly</option>
                    </Input> */}
                  </div>
                </div>
                <div style={{ width: '100%', height: '300px' }}>
                  <ResponsiveContainer>
                    <AreaChart data={dashboardData.purchaseData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3D607A" stopOpacity={0.8} />
                          <stop offset="95%" stopColor="#3D607A" stopOpacity={0.1} />
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="name" axisLine={false} tickLine={false} />
                      <YAxis axisLine={false} tickLine={false} />
                      <CartesianGrid vertical={false} stroke="#eee" />
                      <Tooltip />
                      <Area type="monotone" dataKey="value" stroke="#3D607A" fillOpacity={1} fill="url(#colorUv)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col md={4}>
            <Card body className="order-card">
              <CardBody>
                <div className="d-flex justify-content-between mb-3">
                  <div>
                    <div className="text-muted small">Latest Order</div>
                    <div className="fw-bold">{dashboardData?.latestOrder?.[0]?.order_number || 'None'}</div>
                  </div>
                  <div className="text-end">
                    <div className="text-muted small">{dashboardData?.latestOrder?.[0]?.status || ''}</div>
                    <div className="fw-bold">{dashboardData?.latestOrder?.[0]?.order_date ? moment(dashboardData?.latestOrder?.[0]?.order_date).format('DD-MM-YYYY') : ''}</div>
                  </div>
                </div>
                <div className="text-muted small mb-2">Order Details</div>
                <ul className="list-unstyled order-list">
                  {dashboardData?.latestOrder?.[0]?.items.map((item, index) => (
                    <li key={index} className="d-flex justify-content-between mb-2">
                      <span>{item.item_name}</span>
                      <span className="text-muted">{item.quantity}</span>
                    </li>
                  ))}
                </ul>
              </CardBody>
            </Card>
          </Col>
          <QuotationsRequested data={dashboardData.latestRFQs} columns={columns} sort={false} add={true} header="Recent Orders" />
        </Row>

        <style>{`
        .trend-card {
          border-radius: 10px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.05);
          background-color: #ffffff;
        }
        .icon-circle {
          width: 40px;
          height: 40px;
          border-radius: 25%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }
        .trend-title {
          font-size: 14px;
          color: #777;
          margin-bottom: 2px;
        }
        .trend-value {
          font-size: 18px;
          font-weight: 600;
          color: #333;
        }
        .chart-card, .order-card {
          height: 400px;
          overflow: hidden;
        }
        .order-list {
          max-height: 250px;
          overflow-y: auto;
        }
      `}</style>
      </div>
    </div>
  );
};

export default Home;